import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, BACKEND_URL } from "../lib/api";
import { AdminGate, AdminShell } from "./AdminGate";
import { toast } from "sonner";
import { ImagePlus, Eye, Trash2, RefreshCw, Megaphone, XCircle, Upload } from "lucide-react";

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#F5B301] placeholder:text-slate-400";
const PAGE = 12;
const fmtDt = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const imgUrl = (path) => `${BACKEND_URL}${path}`;
const TIPOS = ["image/jpeg", "image/png", "image/webp"];

function DivulgacaoInner({ user }) {
  const [lista, setLista] = useState([]);
  const [total, setTotal] = useState(0);
  const [texto, setTexto] = useState("");
  const [textoSalvando, setTextoSalvando] = useState(false);
  const [nome, setNome] = useState("");
  const [file, setFile] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [replaceId, setReplaceId] = useState(null);
  const [carregandoMais, setCarregandoMais] = useState(false);
  const fileRef = useRef(null);
  const replaceRef = useRef(null);

  const load = useCallback(async (skip = 0, acumular = false) => {
    try {
      const r = await api.get("/admin/divulgacao", { params: { skip, limit: PAGE } });
      setTotal(r.data.total || 0);
      setLista((l) => (acumular ? [...l, ...(r.data.imagens || [])] : r.data.imagens || []));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao carregar as imagens");
    }
  }, []);

  useEffect(() => {
    load(0);
    api.get("/admin/divulgacao-texto").then((r) => setTexto(r.data.texto || "")).catch(() => {});
  }, [load]);

  const carregarMais = async () => {
    setCarregandoMais(true);
    await load(lista.length, true);
    setCarregandoMais(false);
  };

  const validar = (f) => {
    if (!f) return false;
    if (!TIPOS.includes(f.type)) { toast.error("Formato inválido. Use JPG, PNG ou WebP."); return false; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Imagem muito grande (máx. 10 MB)"); return false; }
    return true;
  };

  const enviar = async () => {
    if (enviando) return;
    if (!validar(file)) { if (!file) toast.error("Selecione uma imagem do dispositivo (JPG, PNG ou WebP)"); return; }
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("nome", nome);
      await api.post("/admin/divulgacao", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Imagem cadastrada! Toque em PUBLICAR para liberá-la aos associados.");
      setFile(null);
      setNome("");
      if (fileRef.current) fileRef.current.value = "";
      load(0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao enviar a imagem. Tente novamente.");
    } finally {
      setEnviando(false);
    }
  };

  const togglePublicacao = async (img) => {
    try {
      await api.put(`/admin/divulgacao/${img.imagem_id}`, { publicado: !img.publicado });
      toast.success(img.publicado ? "Imagem desativada — não aparece mais para os associados." : "Imagem publicada para todos os associados!");
      load(0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao atualizar");
    }
  };

  const excluir = async (img) => {
    if (!window.confirm(`Excluir a imagem "${img.nome}"?\n\nEla deixará de aparecer para os associados. Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/admin/divulgacao/${img.imagem_id}`);
      toast.success("Imagem excluída.");
      setViewer(null);
      load(0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao excluir");
    }
  };

  const substituir = (img) => {
    setReplaceId(img.imagem_id);
    if (replaceRef.current) replaceRef.current.value = "";
    replaceRef.current?.click();
  };

  const onReplace = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !replaceId) return;
    if (!validar(f)) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.put(`/admin/divulgacao/${replaceId}/imagem`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Imagem substituída com sucesso!");
      setViewer(null);
      load(0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao substituir a imagem.");
    } finally {
      setReplaceId(null);
    }
  };

  const salvarTexto = async () => {
    if (textoSalvando) return;
    setTextoSalvando(true);
    try {
      await api.put("/admin/divulgacao-texto", { texto });
      toast.success("Texto promocional salvo! O link de cada associado continua sendo incluído automaticamente.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar o texto");
    } finally {
      setTextoSalvando(false);
    }
  };

  return (
    <AdminShell user={user} title="Imagens de Divulgação">
      <p className="px-4 text-[11px] text-slate-500 -mt-1 mb-1">Gerenciar Imagens de Divulgação — Clube de Indicação</p>
      <div className="px-4 space-y-4" data-testid="admin-divulgacao">

        <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="divulgacao-texto-card">
          <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2 flex items-center gap-1.5"><Megaphone className="w-3.5 h-3.5" /> Texto promocional padrão</h2>
          <p className="text-[10px] text-slate-400 mb-2">O associado pode ajustar o texto antes de compartilhar. O link individual de indicação é sempre incluído automaticamente pelo aplicativo.</p>
          <textarea data-testid="divulgacao-texto-input" rows={4} maxLength={600} className={inputCls} value={texto} onChange={(e) => setTexto(e.target.value)} />
          <button onClick={salvarTexto} disabled={textoSalvando} data-testid="divulgacao-texto-salvar"
            className="mt-2 w-full py-3 rounded-xl bg-navy text-white text-xs font-extrabold active:scale-95 transition-transform disabled:opacity-50">
            {textoSalvando ? "SALVANDO..." : "SALVAR TEXTO PROMOCIONAL"}
          </button>
        </section>

        <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="divulgacao-upload-card">
          <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2 flex items-center gap-1.5"><ImagePlus className="w-3.5 h-3.5" /> Adicionar nova imagem</h2>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="divulgacao-file-input"
            onChange={(e) => { const f = e.target.files?.[0]; if (f && validar(f)) setFile(f); }} />
          <button onClick={() => fileRef.current?.click()} data-testid="divulgacao-escolher-btn"
            className="w-full py-4 rounded-xl border-2 border-dashed border-[#F5B301] bg-gold-light/40 text-navy text-xs font-extrabold flex items-center justify-center gap-2 active:scale-95 transition-transform">
            <Upload className="w-4 h-4" /> {file ? `SELECIONADA: ${file.name}` : "ANEXAR IMAGEM DO DISPOSITIVO"}
          </button>
          {file && (
            <div className="mt-2 bg-slate-100 rounded-xl p-2 flex items-center justify-center">
              <img src={URL.createObjectURL(file)} alt="Prévia" className="max-h-40 object-contain" data-testid="divulgacao-preview" />
            </div>
          )}
          <input className={`${inputCls} mt-2`} placeholder="Nome/identificação da imagem (opcional)" data-testid="divulgacao-nome-input" value={nome} onChange={(e) => setNome(e.target.value)} />
          <p className="text-[10px] text-slate-400 mt-1.5">Formatos: JPG, PNG ou WebP · até 10 MB · sem limite de quantidade.</p>
          <button onClick={enviar} disabled={enviando} data-testid="divulgacao-enviar-btn"
            className="mt-2 w-full py-3 rounded-xl bg-[#F5B301] text-navy text-xs font-extrabold active:scale-95 transition-transform disabled:opacity-50">
            {enviando ? "ENVIANDO..." : "CADASTRAR IMAGEM"}
          </button>
        </section>

        <section data-testid="divulgacao-galeria-admin">
          <div className="flex items-center justify-between mb-2 px-1">
            <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400">Galeria ({total} {total === 1 ? "imagem" : "imagens"})</h2>
            <button onClick={() => load(0)} className="text-[10px] font-bold text-navy flex items-center gap-1" data-testid="divulgacao-refresh"><RefreshCw className="w-3 h-3" /> Atualizar</button>
          </div>
          {lista.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-6 bg-white rounded-2xl border border-slate-100" data-testid="divulgacao-vazio">
              Nenhuma imagem cadastrada. Adicione a primeira acima.
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            {lista.map((img) => (
              <div key={img.imagem_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid={`divulgacao-card-${img.imagem_id}`}>
                <button onClick={() => setViewer(img)} className="w-full h-32 bg-slate-100 flex items-center justify-center" data-testid={`divulgacao-ver-${img.imagem_id}`}>
                  <img src={imgUrl(img.url)} alt={img.nome} className="max-h-32 w-full object-contain" loading="lazy" />
                </button>
                <div className="p-2.5">
                  <p className="text-[10px] font-bold text-navy truncate">{img.nome}</p>
                  <p className="text-[9px] text-slate-400">{fmtDt(img.created_at)}</p>
                  <span className={`mt-1 inline-block text-[9px] font-extrabold px-2 py-0.5 rounded-full ${img.publicado ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`} data-testid={`divulgacao-status-${img.imagem_id}`}>
                    {img.publicado ? "PUBLICADA" : "DESATIVADA"}
                  </span>
                  <div className="grid grid-cols-2 gap-1.5 mt-2">
                    <button onClick={() => togglePublicacao(img)} data-testid={img.publicado ? `divulgacao-desativar-${img.imagem_id}` : `divulgacao-publicar-${img.imagem_id}`}
                      className={`py-1.5 rounded-lg text-[9px] font-extrabold active:scale-95 transition-transform ${img.publicado ? "bg-slate-200 text-slate-600" : "bg-emerald-500 text-white"}`}>
                      {img.publicado ? "DESATIVAR" : "PUBLICAR"}
                    </button>
                    <button onClick={() => substituir(img)} data-testid={`divulgacao-substituir-${img.imagem_id}`}
                      className="py-1.5 rounded-lg bg-navy text-white text-[9px] font-extrabold active:scale-95 transition-transform">
                      SUBSTITUIR
                    </button>
                  </div>
                  <button onClick={() => excluir(img)} data-testid={`divulgacao-excluir-${img.imagem_id}`}
                    className="mt-1.5 w-full py-1.5 rounded-lg bg-red-100 text-red-600 text-[9px] font-extrabold flex items-center justify-center gap-1 active:scale-95 transition-transform">
                    <Trash2 className="w-3 h-3" /> EXCLUIR
                  </button>
                </div>
              </div>
            ))}
          </div>
          {lista.length < total && (
            <button onClick={carregarMais} disabled={carregandoMais} data-testid="divulgacao-carregar-mais"
              className="mt-3 w-full py-3 rounded-xl border-2 border-navy text-navy text-xs font-extrabold active:scale-95 transition-transform disabled:opacity-50">
              {carregandoMais ? "CARREGANDO..." : `CARREGAR MAIS (${lista.length} de ${total})`}
            </button>
          )}
        </section>

        <p className="text-center pb-2"><Link to="/admin/indicadores" className="text-[11px] text-slate-400 underline" data-testid="divulgacao-voltar-indicadores">Voltar à Gestão de Indicadores</Link></p>
      </div>

      <input ref={replaceRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="divulgacao-replace-input" onChange={onReplace} />

      {viewer && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center" data-testid="divulgacao-viewer-modal" onClick={() => setViewer(null)}>
          <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-navy text-sm truncate">{viewer.nome}</h3>
              <button onClick={() => setViewer(null)} data-testid="divulgacao-viewer-fechar" className="text-slate-400 hover:text-slate-600"><XCircle className="w-5 h-5" /></button>
            </div>
            <div className="bg-slate-100 rounded-2xl p-2 flex items-center justify-center">
              <img src={imgUrl(viewer.url)} alt={viewer.nome} className="max-h-[55vh] object-contain" data-testid="divulgacao-viewer-img" />
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Cadastrada em {fmtDt(viewer.created_at)} por {viewer.created_by || "—"} · {viewer.publicado ? "PUBLICADA" : "DESATIVADA"}</p>
            <div className="grid grid-cols-3 gap-2 mt-3">
              <button onClick={() => togglePublicacao(viewer).then(() => setViewer((v) => v && { ...v, publicado: !v.publicado }))} data-testid="divulgacao-viewer-toggle"
                className={`py-2.5 rounded-xl text-[10px] font-extrabold active:scale-95 transition-transform ${viewer.publicado ? "bg-slate-200 text-slate-600" : "bg-emerald-500 text-white"}`}>
                {viewer.publicado ? "DESATIVAR" : "PUBLICAR"}
              </button>
              <button onClick={() => substituir(viewer)} data-testid="divulgacao-viewer-substituir"
                className="py-2.5 rounded-xl bg-navy text-white text-[10px] font-extrabold active:scale-95 transition-transform">
                SUBSTITUIR
              </button>
              <button onClick={() => excluir(viewer)} data-testid="divulgacao-viewer-excluir"
                className="py-2.5 rounded-xl bg-red-100 text-red-600 text-[10px] font-extrabold flex items-center justify-center gap-1 active:scale-95 transition-transform">
                <Trash2 className="w-3 h-3" /> EXCLUIR
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}

export default function AdminDivulgacao() {
  return <AdminGate>{(user) => <DivulgacaoInner user={user} />}</AdminGate>;
}
